import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { billingService } from '../../api/services/billing.service';
import { DeferredRevenueDTO, formatMoney } from '../../types/billing';
import { useAuth } from 'hooks/useAuth';
import { formatServerUtcAsIst, istMonth } from './closeBooksRules';
import OrgFilterSelect from './OrgFilterSelect';
import { useLatestRequest } from './latestRequest';
import { deferredRevenueParams, formatCount, isPlatformStaff } from './billingReportRules';

const filterInput =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';

/**
 * Prepaid liability roll-forward for an IST month: opening + top-ups − consumption ±
 * adjustments − expired = closing, per account and totalled per currency. Platform staff
 * see every prepaid account; a provider names one organisation beneath it.
 */
const DeferredRevenue: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const staff = isPlatformStaff((currentOrganization as any)?.role || (user as any)?.role);

  const [month, setMonth] = useState(istMonth());
  const [orgId, setOrgId] = useState(staff ? '' : currentOrganization?.id || '');
  const [report, setReport] = useState<DeferredRevenueDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const beginRequest = useLatestRequest();

  useEffect(() => {
    if (!month) return;
    const isLatest = beginRequest();
    setLoading(true);
    const q = deferredRevenueParams({ month, organizationId: orgId });
    billingService.getDeferredRevenue({ month, organizationId: q.organizationId }).then((r) => {
      if (!isLatest()) return;
      if (r.success && r.data) {
        setReport(r.data);
        setError(null);
      } else {
        setReport(null);
        setError(r.error || 'Failed to load deferred revenue');
      }
      setLoading(false);
    });
  }, [month, orgId, beginRequest]);

  const download = async () => {
    setBusy(true);
    setDownloadError(null);
    const q = deferredRevenueParams({ month, organizationId: orgId });
    const r = await billingService.downloadDeferredRevenue({ month, organizationId: q.organizationId });
    if (!r.ok) setDownloadError(r.error);
    setBusy(false);
  };

  const cols: { key: 'opening' | 'topUps' | 'consumption' | 'adjustments' | 'expired' | 'closing'; label: string }[] = [
    { key: 'opening', label: 'Opening' },
    { key: 'topUps', label: 'Top-ups' },
    { key: 'consumption', label: 'Consumption' },
    { key: 'adjustments', label: 'Adjustments' },
    { key: 'expired', label: 'Expired' },
    { key: 'closing', label: 'Closing' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Deferred revenue</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Prepaid credit still owed to customers: the month's roll-forward per account (IST month).
          {report ? ` ${formatServerUtcAsIst(report.fromUtc)} – ${formatServerUtcAsIst(report.toUtc)} IST.` : ''}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className={labelCls}>Month (IST)</label>
          <input
            type="month"
            aria-label="Month (IST)"
            value={month}
            max={istMonth()}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            className={filterInput}
          />
        </div>
        {staff && <OrgFilterSelect value={orgId} onChange={setOrgId} allLabel="All prepaid accounts" />}
        <button
          onClick={download}
          disabled={busy || !month}
          className="self-end inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
        >
          <FileText className="h-4 w-4 mr-2" />
          {busy ? 'Preparing…' : 'Download .xlsx'}
        </button>
      </div>
      {downloadError && <p className="text-sm text-red-600">{downloadError}</p>}

      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : loading && !report ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
        </div>
      ) : report ? (
        <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto ${loading ? 'opacity-60' : ''}`}>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className={`${th} text-left`}>Organization</th>
                <th className={`${th} text-left`}>Type</th>
                {cols.map((c) => <th key={c.key} className={`${th} text-right`}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {report.accounts.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-gray-500">No prepaid accounts</td></tr>
              ) : (
                report.accounts.map((a) => (
                  <tr key={a.organizationId}>
                    <td className={td}>{a.organizationName || a.organizationId}</td>
                    <td className={td}>{a.billingType || '-'}</td>
                    {cols.map((c) => (
                      <td key={c.key} className={`${td} text-right`}>{formatMoney(a[c.key], a.currency)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-gray-50 dark:bg-gray-700">
              {report.totals.map((t) => (
                <tr key={t.currency}>
                  <td className={`${td} font-semibold`}>Total {t.currency}</td>
                  <td className={td}>{formatCount(t.accounts)} account(s)</td>
                  {cols.map((c) => (
                    <td key={c.key} className={`${td} text-right font-semibold`}>{formatMoney(t[c.key], t.currency)}</td>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default DeferredRevenue;
